import { env } from '@config/env';

export interface ApiClientOptions {
  getAccessToken: () => Promise<string | null>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body: unknown,
  ) {
    // Prefer the server's ProblemDetails message (RFC 7807 `detail`, then `title`) so the UI shows
    // the real reason instead of a generic "API error 400: Bad Request".
    super(ApiError.messageFrom(body) ?? `API error ${status}: ${statusText}`);
  }

  private static messageFrom(body: unknown): string | null {
    if (body && typeof body === 'object') {
      const p = body as { detail?: unknown; title?: unknown };
      if (typeof p.detail === 'string' && p.detail.trim()) return p.detail;
      if (typeof p.title === 'string' && p.title.trim()) return p.title;
    }
    return null;
  }
}

/**
 * A request that never reached the server: `fetch` rejected (DNS failure, connection refused, a CORS
 * preflight that got no answer, a dropped VPN). Distinct from `ApiError`, which is an answer with an error
 * status. Thrown by the calls that refuse the mock fallback (the Compliance console) so the screen can say
 * exactly this, with one fixed message, instead of the browser's "Failed to fetch".
 */
export class BackendUnreachableError extends Error {
  static readonly MESSAGE = 'Cannot reach the backend: the admin API did not answer. Nothing here is live, and no sample data is shown in its place.';

  constructor(cause?: unknown) {
    super(BackendUnreachableError.MESSAGE, { cause });
    this.name = 'BackendUnreachableError';
  }
}

export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  async get<T>(path: string, init?: RequestInit): Promise<T> {
    return this.send<T>('GET', path, undefined, init);
  }

  async post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.send<T>('POST', path, body, init);
  }

  async put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.send<T>('PUT', path, body, init);
  }

  async del<T>(path: string, init?: RequestInit): Promise<T> {
    return this.send<T>('DELETE', path, undefined, init);
  }

  private async send<T>(method: string, path: string, body: unknown, init?: RequestInit): Promise<T> {
    const url = new URL(path, env.apiBaseUrl).toString();
    const headers = await this.buildAuthHeaders(body !== undefined, init?.headers);

    const response = await fetch(url, {
      ...init,
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('json') ? await response.json() : await response.text();

    if (!response.ok) {
      throw new ApiError(response.status, response.statusText, payload);
    }

    return payload as T;
  }

  private async buildAuthHeaders(hasJsonBody: boolean, initHeaders?: HeadersInit): Promise<Headers> {
    const headers = new Headers(initHeaders);
    headers.set('Accept', 'application/json');
    if (hasJsonBody) headers.set('Content-Type', 'application/json');

    const token = await this.opts.getAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }
}
